// =============================================================================
// MeuDriver — useQuiz
// State machine para o simulado de questões de trânsito.
//
// Estados: idle → loading → question → result
//
// Uso:
//   const { state, currentQuestion, answerQuestion, nextQuestion, finishQuiz,
//           startQuiz, session, answers } = useQuiz()
// =============================================================================

import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { QuizQuestionRow, QuizCategory, QuizSessionRow } from '@/types/database'
import { toast } from 'sonner'

export type QuizState = 'idle' | 'loading' | 'question' | 'result'

export interface QuizAnswerRecord {
  question: QuizQuestionRow
  selectedOption: string
  isCorrect: boolean
}

export interface UseQuizReturn {
  state: QuizState
  questions: QuizQuestionRow[]
  currentIndex: number
  currentQuestion: QuizQuestionRow | null
  session: QuizSessionRow | null
  answers: QuizAnswerRecord[]
  /** Inicia um novo quiz. Passa category para filtrar ou null para aleatório. */
  startQuiz: (category?: QuizCategory | null) => Promise<void>
  /** Registra a resposta do aluno para a questão atual. */
  answerQuestion: (optionId: string) => Promise<void>
  /** Avança para a próxima questão. */
  nextQuestion: () => void
  /** Encerra o quiz e persiste o resultado. */
  finishQuiz: () => Promise<void>
  isLoading: boolean
}

const QUESTIONS_PER_SESSION = 20

/** Embaralha um array (Fisher-Yates). */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export function useQuiz(): UseQuizReturn {
  const { user } = useAuth()
  const [state, setState] = useState<QuizState>('idle')
  const [questions, setQuestions] = useState<QuizQuestionRow[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [session, setSession] = useState<QuizSessionRow | null>(null)
  const [answers, setAnswers] = useState<QuizAnswerRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const currentQuestion = questions[currentIndex] ?? null

  const startQuiz = useCallback(async (category?: QuizCategory | null) => {
    if (!user?.id) {
      toast.error('Você precisa estar logado para fazer o simulado.')
      return
    }

    setState('loading')
    setIsLoading(true)
    setAnswers([])
    setCurrentIndex(0)

    try {
      // Busca questões ativas (filtra por categoria se informada)
      let query = supabase.from('quiz_questions').select('*').eq('active', true)
      if (category) query = query.eq('category', category)

      const { data: rawQuestions, error: qErr } = await query
      if (qErr) throw new Error(qErr.message)

      const sampled = shuffle(rawQuestions ?? [])
        .slice(0, QUESTIONS_PER_SESSION) as QuizQuestionRow[]

      if (sampled.length === 0) {
        toast.error('Nenhuma questão disponível para esta categoria.')
        setState('idle')
        return
      }

      // Cria a sessão no banco
      const { data: newSession, error: sErr } = await supabase
        .from('quiz_sessions')
        .insert({
          user_id: user.id,
          category: category ?? null,
          total_questions: sampled.length,
          correct_answers: 0,
        })
        .select('*')
        .single()

      if (sErr || !newSession) throw new Error(sErr?.message ?? 'Falha ao criar sessão')

      setQuestions(sampled)
      setSession(newSession as unknown as QuizSessionRow)
      setState('question')
    } catch (err) {
      console.error('[useQuiz] startQuiz error:', err)
      toast.error('Erro ao carregar o simulado. Tente novamente.')
      setState('idle')
    } finally {
      setIsLoading(false)
    }
  }, [user?.id])

  const answerQuestion = useCallback(async (optionId: string) => {
    if (!session || !currentQuestion || !user?.id) return

    const isCorrect = optionId === currentQuestion.correct_option

    const record: QuizAnswerRecord = {
      question: currentQuestion,
      selectedOption: optionId,
      isCorrect,
    }

    setAnswers((prev) => [...prev, record])

    // Persiste a resposta no banco (best-effort, não bloqueia a UI)
    await supabase.from('quiz_answers').insert({
      session_id: session.id,
      question_id: currentQuestion.id,
      selected_option: optionId,
      is_correct: isCorrect,
    }).then(({ error }) => {
      if (error) console.warn('[useQuiz] answerQuestion DB error:', error.message)
    })
  }, [session, currentQuestion, user?.id])

  const nextQuestion = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1)
    }
  }, [currentIndex, questions.length])

  const finishQuiz = useCallback(async () => {
    if (!session) return

    setIsLoading(true)
    const correctCount = answers.filter((a) => a.isCorrect).length
    const totalCount = answers.length

    try {
      await supabase
        .from('quiz_sessions')
        .update({
          total_questions: totalCount,
          correct_answers: correctCount,
          completed_at: new Date().toISOString(),
        })
        .eq('id', session.id)

      setSession((prev) =>
        prev
          ? { ...prev, total_questions: totalCount, correct_answers: correctCount, completed_at: new Date().toISOString() }
          : prev,
      )
    } catch (err) {
      console.error('[useQuiz] finishQuiz error:', err)
    } finally {
      setIsLoading(false)
      setState('result')
    }
  }, [session, answers])

  return {
    state,
    questions,
    currentIndex,
    currentQuestion,
    session,
    answers,
    startQuiz,
    answerQuestion,
    nextQuestion,
    finishQuiz,
    isLoading,
  }
}
